import classnames from 'classnames';
import { useMemo, useState } from 'react';
import type { Price, PricingFormMode, Tier } from '../../types';
import * as styles from '../Pricing.css';
import type { TPricingAttributes } from '../Pricing.types';
import PriceBlock from './PriceBlock';
import { RecommendedBadge } from './RecommendedBadge';

interface PriceCardProps {
  tier: Tier;
  selectedTier?: Tier;
  selectedPrice?: Price;
  attributes: TPricingAttributes;
  setSelectedPrice: (price: Price) => void;
  setSelectedTier: (tier: Tier) => void;
  isInternalCheckout: boolean;
  donationAmountCents: number;
  setDonationAmountCents: (value: number) => void;
  isCtaDisabled: boolean;
  currentFormMode?: PricingFormMode;
}

const TierCard = ({
  tier,
  selectedTier,
  selectedPrice,
  attributes,
  isInternalCheckout,
  setSelectedPrice,
  setSelectedTier,
  donationAmountCents,
  setDonationAmountCents,
  isCtaDisabled,
  currentFormMode,
}: PriceCardProps) => {
  // Price ids in tier
  const priceIds = useMemo(() => {
    return tier.prices.map(({ id }) => id);
  }, [tier.prices]);

  const [visiblePriceId, setVisiblePriceId] = useState<string | null>(
    selectedPrice?.id && priceIds.includes(selectedPrice.id) ? selectedPrice.id : null,
  );

  // Evaluate whether the tier is selected based on tier id and prices in a tier
  const isSelected = useMemo(() => {
    const priceId = visiblePriceId || selectedPrice?.id;
    return tier.id === selectedTier?.id && priceIds.includes(priceId || '');
  }, [tier, selectedTier, visiblePriceId, selectedPrice, priceIds]);

  const visiblePrice = useMemo(() => {
    if (selectedPrice && priceIds.includes(selectedPrice.id)) {
      return tier.prices.find((price) => price.id === selectedPrice.id);
    }

    return visiblePriceId && priceIds.includes(visiblePriceId)
      ? tier.prices.find((price) => price.id === visiblePriceId)
      : tier.prices[0];
  }, [visiblePriceId, tier]);

  const handleClickTier = () => {
    setSelectedTier?.(tier);

    if (visiblePriceId === selectedPrice?.id) {
      return;
    }

    // Price is not selected
    const foundPrice = visiblePriceId ? tier.prices.find(({ id }) => id === visiblePriceId) : tier.prices[0];
    if (foundPrice) {
      setSelectedPrice?.(foundPrice);
    }
  };

  const handleClickPrice = (price: Price) => {
    setVisiblePriceId(price.id);
    setSelectedPrice?.(price);

    if (!selectedTier || selectedTier?.id !== tier.id) {
      setSelectedTier(tier);
    }
  };

  const shouldShowRecommendedBadge =
    attributes?.recommendedObjectType === 'tier' && attributes?.recommendedTierId === tier.id;

  return (
    <div className="w-auto">
      <div
        className={classnames('flex flex-col w-72 lg:w-80 xl:w-96 relative cursor-pointer gap-4', styles.card)}
        style={{
          borderStyle: attributes.cardBorderStyle,
          borderWidth: attributes.cardBorderWidth,
          borderColor: isSelected ? attributes.cardSelectedBorderColor : attributes.cardBorderColor,
          borderRadius: attributes.cardBorderRadius,
          ...(isSelected ? { backgroundColor: attributes.cardSelectedBackgroundColor } : {}),
        }}
        onClick={handleClickTier}
        onKeyDown={handleClickTier}
      >
        {shouldShowRecommendedBadge && <RecommendedBadge text={attributes?.recommendedBadgeText || 'Recommended'} />}
        <PriceBlock
          attributes={attributes}
          tier={tier}
          isInternalCheckout={isInternalCheckout}
          selectedPrice={visiblePrice}
          donationAmountCents={donationAmountCents}
          setDonationAmountCents={setDonationAmountCents}
          isCtaDisabled={isCtaDisabled}
          onClickPriceTab={handleClickPrice}
          isSelected={isSelected}
          currentFormMode={currentFormMode}
          hasDiscordAccess={tier.discord_access}
        />
      </div>
    </div>
  );
};

export default TierCard;
